import { AttioValuationItemQueryResponse } from '../attio.types';
import { AttioValuationDetailResponse } from '../shared-types';
import { AttioCompanyService } from './AttioCompanyService';
import { AttioConsultantService } from './AttioConsultantService';
import { AttioDealService } from './AttioDealService';
import { AttioEngagementService } from './AttioEngagementService';
import { AttioPeopleService } from './AttioPeopleService';
import { BaseAttioService } from './BaseAttioService';

export class AttioValuationDetailService extends BaseAttioService {
  private readonly companyService: AttioCompanyService;
  private readonly peopleService: AttioPeopleService;
  private readonly dealService: AttioDealService;
  private readonly engagementService: AttioEngagementService;
  private readonly consultantService: AttioConsultantService;

  constructor() {
    super('AttioValuationDetailService');
    this.companyService = new AttioCompanyService();
    this.peopleService = new AttioPeopleService();
    this.dealService = new AttioDealService();
    this.engagementService = new AttioEngagementService();
    this.consultantService = new AttioConsultantService();
  }

  /**
   * Get a single valuation item with related data
   */
  async getValuationDetail(valuationRecordId: string): Promise<AttioValuationDetailResponse> {
    try {
      const response = await this.get<AttioValuationItemQueryResponse>(
        this.endpoints.retrieveValuationByRecordId(valuationRecordId)
      );

      const { data } = response;
      const { values: valuationData } = data;

      // Extract target record IDs
      const valuedEntityRecordId = valuationData.valued_entity?.[0]?.target_record_id;
      const valuationLeadRecordId = valuationData.valuation_lead_94?.[0]?.target_record_id;
      const dealRecordId = valuationData.deal?.[0]?.target_record_id;
      const otherConsultantRecordId = valuationData.other_consultant?.[0]?.target_record_id;
      const engagementManagerRecordId = valuationData.engagement_manager?.[0]?.target_record_id;

      const valuationUrl = data.web_url;

      // Initialize base response
      const baseResponse: AttioValuationDetailResponse = {
        valuedEntity: {
          valuedEntityId: valuedEntityRecordId || '-',
          companyName: valuationData.identifier?.[0]?.value || '-',
          primaryContact: '-',
          primaryContactId: undefined,
          category: valuationData.engagement_category?.[0]?.option?.title || '-',
          jobTitle: '-',
          salesRep: '',
          salesRepImageSrc: '',
          companyUrl: undefined,
          valuedEntityNotice: undefined,
          primaryContactNotice: undefined,
        },
        valuedInformation: {
          valuationDate: valuationData.valuation_date?.[0]?.value || '-',
          valuationName: valuationData.identifier?.[0]?.value || '-',
          bookingType: valuationData.booking_type?.[0]?.option?.title || '-',
          bookingDate: valuationData.booking_date?.[0]?.value || '-',
          valuationLeadName: '-',
          valuationLeadImageSrc: '-',
          valuationId: valuationData.record_id?.[0]?.value || '-',
          renewalDate: '-',
          valuationUrl,
          otherConsultantName: '-',
          otherConsultantImageSrc: '-',
          otherConsultantEmail: '-',
          valuationNotice: valuationData.notice?.[0]?.value || undefined,
          valuationFee: valuationData.valuation_fee?.[0]?.currency_value || 0,
          valuationNetValue: valuationData.net_value?.[0]?.currency_value?.toString() || '-',
          valuationEngagementManager: '-',
          valuationEngagementManagerImageSrc: '-',
          valuationEngagementManagerRole: '-',
          // Financial fields
          split: valuationData.split?.[0]?.currency_value,
          payment1: valuationData.payment_1?.[0]?.currency_value,
          payment1Date: valuationData.pay_1_date?.[0]?.value,
          payment2: valuationData.payment_2?.[0]?.currency_value,
          payment2Date: valuationData.pay_2_date?.[0]?.value,
          payment3: valuationData.payment_3?.[0]?.currency_value,
          payment3Date: valuationData.pay_3_date?.[0]?.value,
          refund: valuationData.refund?.[0]?.currency_value,
          refundDate: valuationData.refund_date?.[0]?.value,
          due: valuationData.due?.[0]?.currency_value,
          adjustment: valuationData.adjustment?.[0]?.currency_value,
          adjustmentDate: valuationData.adjustment_date?.[0]?.value,
          badDebt: valuationData.bad_debt?.[0]?.currency_value,
          badDebtDate: valuationData.bad_debt_date?.[0]?.value,
          renewalRate: valuationData.renewal_rate?.[0]?.currency_value,
        },
        deal: {
          dealId: dealRecordId || '-',
          valuationDate: valuationData.valuation_date?.[0]?.value || '-',
          dealTotal: '-',
          dealStage: '-',
          engagementManager: '-',
          engagementManagerImageSrc: '-',
          engagementManagerRole: '-',
          dealUrl: undefined,
          dealNotice: undefined,
          due: '-',
          net: '-',
          contractStatus: '-',
          signedPDF: undefined,
          elURL: undefined,
          dealDate: '-',
          dealStageOptions: [],
        },
        status: valuationData.stage?.[0]?.option?.title || '-',
        statusId: valuationData.stage?.[0]?.option?.id?.option_id || undefined,
      };

      try {
        const round1 = await this.fetchRelatedData({
          valuedEntityRecordId,
          valuationLeadRecordId,
          dealRecordId,
          otherConsultantRecordId,
          engagementManagerRecordId,
        });
        baseResponse.valuedEntity = { ...baseResponse.valuedEntity, ...round1.valuedEntityPatch };
        baseResponse.valuedInformation = { ...baseResponse.valuedInformation, ...round1.valuedInformationPatch };
        baseResponse.deal = { ...baseResponse.deal, ...round1.dealPatch };

        const round2 = await this.fetchDependentData(round1);
        baseResponse.valuedEntity = { ...baseResponse.valuedEntity, ...round2.valuedEntityPatch };
        baseResponse.deal = { ...baseResponse.deal, ...round2.dealPatch };

        return baseResponse;
      } catch (error) {
        console.warn('Error fetching additional data, returning base response:', error);
        return baseResponse;
      }
    } catch (error) {
      this.handleError(error, 'getValuationDetail');
      throw error; // Re-throw after handling
    }
  }

  private async fetchRelatedData(ids: {
    valuedEntityRecordId?: string;
    valuationLeadRecordId?: string;
    dealRecordId?: string;
    otherConsultantRecordId?: string;
    engagementManagerRecordId?: string;
  }) {
    const { valuedEntityRecordId, valuationLeadRecordId, dealRecordId, otherConsultantRecordId, engagementManagerRecordId } = ids;

    const [companyData, valuationEngagementManagerData, valuationLeadData, dealData, dealStageOptions, dealContractData, otherConsultantData] =
      await Promise.all([
        valuedEntityRecordId ? this.companyService.getCompanyData(valuedEntityRecordId).catch(() => null) : Promise.resolve(null),
        engagementManagerRecordId ? this.engagementService.getValuationEngagementManager(engagementManagerRecordId).catch(() => null) : Promise.resolve(null),
        valuationLeadRecordId ? this.consultantService.getOtherConsultant(valuationLeadRecordId).catch(() => null) : Promise.resolve(null),
        dealRecordId ? this.dealService.getDealData(dealRecordId).catch(() => null) : Promise.resolve(null),
        dealRecordId ? this.dealService.getDealStatuses().catch(() => null) : Promise.resolve(null),
        dealRecordId ? this.dealService.getDealContractDataWithSpecificList(dealRecordId).catch(() => null) : Promise.resolve(null),
        otherConsultantRecordId ? this.consultantService.getOtherConsultant(otherConsultantRecordId).catch(() => null) : Promise.resolve(null),
      ]);

    const contractEntry = dealContractData?.data[0]?.entry_values;

    const valuedEntityPatch = {
      ...(companyData && {
        companyName: companyData.companyName,
        companyUrl: companyData.companyUrl,
        valuedEntityNotice: companyData.notes?.[0]?.value || undefined,
      }),
    };

    const valuedInformationPatch = {
      ...(valuationEngagementManagerData && {
        valuationEngagementManager: valuationEngagementManagerData.name,
        valuationEngagementManagerImageSrc: valuationEngagementManagerData.imageSrc,
        valuationEngagementManagerRole: valuationEngagementManagerData.role,
      }),
      ...(valuationLeadData && {
        valuationLeadName: valuationLeadData.name,
        valuationLeadImageSrc: valuationLeadData.imageSrc,
      }),
      ...(dealData && { valuationNetValue: dealData.net || '-' }),
      ...(otherConsultantData && {
        otherConsultantName: otherConsultantData.name,
        otherConsultantImageSrc: otherConsultantData.imageSrc,
        otherConsultantEmail: otherConsultantData.email,
      }),
    };

    const dealPatch = {
      ...(dealData && {
        dealUrl: dealData.dealUrl,
        dealName: dealData.dealName,
        dealTotal: dealData.dealTotal,
        dealStage: dealData.dealStage,
        due: dealData.due,
        dealDate: dealData.dealDate,
        net: dealData.net,
        dealNotice: dealData.notice?.[0]?.value || undefined,
        signedPDF: dealData.signedPDF,
      }),
      ...(dealStageOptions && { dealStageOptions: dealStageOptions.data }),
      ...(contractEntry && {
        contractStatus: contractEntry.contract_status[0]?.status?.title || undefined,
        elURL: contractEntry.doc_link?.[0]?.value || undefined,
      }),
    };

    return { valuedEntityPatch, valuedInformationPatch, dealPatch, companyData, dealEngagementManagerId: dealData?.engagementManagerId };
  }

  private async fetchDependentData(round1: {
    companyData: Awaited<ReturnType<AttioCompanyService['getCompanyData']>> | null;
    dealEngagementManagerId?: string;
  }) {
    const { companyData, dealEngagementManagerId } = round1;
    const [salesRepData, primaryContactData, dealEngagementManagerData] = await Promise.all([
      companyData?.salesRepId ? this.engagementService.getEngagementManager(companyData.salesRepId).catch(() => null) : Promise.resolve(null),
      companyData?.primaryContactId ? this.peopleService.getPeopleData(companyData.primaryContactId).catch(() => null) : Promise.resolve(null),
      dealEngagementManagerId ? this.engagementService.getEngagementManager(dealEngagementManagerId).catch(() => null) : Promise.resolve(null),
    ]);

    const valuedEntityPatch = {
      ...(salesRepData && { salesRep: salesRepData.name, salesRepImageSrc: salesRepData.imageSrc }),
      ...(primaryContactData && {
        primaryContact: primaryContactData.name,
        primaryContactId: companyData?.primaryContactId,
        jobTitle: primaryContactData.jobTitle,
        primaryContactNotice: primaryContactData.notes?.[0]?.value || undefined,
      }),
    };

    const dealPatch = {
      ...(dealEngagementManagerData && {
        engagementManager: dealEngagementManagerData.name,
        engagementManagerImageSrc: dealEngagementManagerData.imageSrc,
      }),
    };

    return { valuedEntityPatch, dealPatch };
  }

  async getValuationByRecordId(valuationRecordId: string): Promise<AttioValuationItemQueryResponse> {
    try {
      const response = await this.get<AttioValuationItemQueryResponse>(
        this.endpoints.retrieveValuationByRecordId(valuationRecordId)
      );
      return response;
    } catch (error) {
      this.handleError(error, 'getValuationByRecordId');
      throw error;
    }
  }
}
